import React, { useState, useEffect } from 'react';
import { Row, Col, Progress, Tooltip, Tag, Switch, Space, Typography, Pagination } from 'antd';
import Text from 'antd/lib/typography/Text';
import { ShoppingCartOutlined, UserAddOutlined, EyeOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import {
  getCTRFromAggregate,
  formatWholeNumber,
  dailyTotalSpentFromAggregate,
  formatCurrency,
  getPacingGoal,
  getDailyPacing,
  campaignOrderPreCalculate,
  totalSpentFromAggregate,
  formatNumber2SigFig,
  formatCurrency2SigFig,
  getEffectiveCpm,
} from '../../../../core/utils/campaigns.js';
// Global Styles
import {
  TableColumnTitle,
  PrimaryTableRowText,
  SecondaryTableRowText,
  FlightDateText,
  StandardTableRowText,
} from '../../../shared/globalStyling/styledText';
import PaginationTable from '../../../shared/PaginationTable';
import moment from 'moment';
import MiniChart from '../../../shared/MiniChart';
import { isEmpty, isNil } from 'lodash';
import { aggregateDisplayReports } from '../../../../core/utils/dataManipulating/aggregateDisplayReports';
import { TableColumnsDropDown } from './TableColumnsDropDown';
import StatusTag, { deriveStatus } from '../shared/StatusTag';
import { ReactComponent as ReactLogo } from '../../../../assets/icons/campaigns/retaillocation.svg';
import { ReactComponent as WalkinsEnabled } from '../../../../assets/icons/campaigns/walkins_enabled.svg';
import { ReactComponent as SignUpEnabled } from '../../../../assets/icons/campaigns/signup_2.svg';
import { ReactComponent as ShoppingCartBasketItem } from '../../../../assets/icons/campaigns/shopping-cart-basket-item.svg';
import { ReactComponent as ShoppingCartBasketItemLocation } from '../../../../assets/icons/campaigns/shopping-cart-basket-item-location.svg';
import { primaryTextStyle } from '../../../shared/globalStyling/style.js';
import { hasSpecialCharacters, formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import './CampaignsTable.scss';
import CampaignCard from './CampaignCard';

const Frag = React.Fragment;

const CampaignsTable = ({
  data,
  history,
  possibleFields,
  selectedTableColumns,
  onTableColChange,
  location,
  total,
  current,
  selectedRowKeys,
  onRowChange,
  currencyFormat,
  selectedRows,
  currentUser,
  switchPacingYesterday,
  setSwitchPacingYesterday,
}) => {
  const { timezone } = useTimezone();
  const params = new URLSearchParams(window.location.search);
  const getSortOrder = (key) => {
    const sortParams = params.get('sort');
    const splitSort = sortParams && sortParams.split('_');
    return sortParams && splitSort[0] === key
      ? splitSort[1] === 'DESC'
        ? 'descend'
        : 'ascend'
      : false;
  };
  const pageSize = params.get('pageSize') ? parseInt(params.get('pageSize')) : 10;
  //trigger this when you want to change column render without triggering the sort
  let isColumnChange = false;

  // Get switch states from URL parameters
  const switchStateSales = params.get('salesSwitch') === 'true';
  const setSwitchStateSales = (value) => {
    const newParams = new URLSearchParams(window.location.search);
    if (value) {
      newParams.set('salesSwitch', 'true');
    } else {
      newParams.delete('salesSwitch');
    }
    history.push({
      pathname: location.pathname,
      search: `?${newParams.toString()}`,
    });
  };

  const switchStateBudget = params.get('budgetSwitch') === 'true';
  const setSwitchStateBudget = (value) => {
    const newParams = new URLSearchParams(window.location.search);
    if (value) {
      newParams.set('budgetSwitch', 'true');
    } else {
      newParams.delete('budgetSwitch');
    }
    history.push({
      pathname: location.pathname,
      search: `?${newParams.toString()}`,
    });
  };

  const [switchState, setSwitchState] = useState(false);
  const [footerBudgetState, setFooterBudgetState] = useState(false);
  const [screenSize, setScreenSize] = useState('desktop');

  // Screen size detection for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 1199) {
        setScreenSize('cards'); // Use cards for both mobile and tablet
      } else {
        setScreenSize('desktop'); // Use full table for desktop
      }
    };

    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get all columns for desktop table view
  const getDesktopColumns = (fieldsToRender) => {
    return fieldsToRender.map((key) => {
      const field = possibleFields[key];
      return getColumn[field.title] ? getColumn[field.title](field) : getColumn.generic(field);
    });
  };

  const getColumn = {
    Name: (field) => {
      return {
        title: <TableColumnTitle text={'Name'} />,
        dataIndex: 'name',
        key: 'name',
        width: '15%',
        align: 'left',
        render: (text, record) => {
          const getIsRequestChange = record.isRequestChange && record.isRequestChange;

          // Apply naming convention if campaign name doesn't have special characters
          const displayName = !hasSpecialCharacters(text) ? formatCampaignName(record, timezone) : text;

          const orgs = record.orgs;
          let sharedOrgs;
          if (orgs) {
            let count = 0;
            const maxOrgTags = 2;
            let moreOrgs = [];

            sharedOrgs = orgs.map((org) => {
              if (count < maxOrgTags) {
                count++;
                return (
                  <Tag key={org.name} color="gray">
                    {org.name}
                  </Tag>
                );
              } else if (count >= maxOrgTags) {
                count++;
                moreOrgs.push(<div key={org.name}>{org.name}</div>);
              }
              return null;
            });

            if (count > maxOrgTags) {
              sharedOrgs.push(
                <Tooltip key="moreOrgs" placement="right" title={<>{moreOrgs}</>}>
                  <Tag color="gray">+{count - maxOrgTags} more</Tag>
                </Tooltip>
              );
            }
          }

          return (
            <Frag>
              <Row type="flex" justify="start">
                <Col style={{ zIndex: 1, width: '100%' }}>
                  {sharedOrgs && sharedOrgs.length > 0 && (
                    <Row
                      style={{
                        overflowX: 'visible',
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        marginBottom: '5px',
                      }}
                    >
                      {sharedOrgs}
                    </Row>
                  )}
                  <div
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    <a
                      href={`/campaigns/details/${record.id}`}
                      style={{
                        cursor: 'pointer',
                        color: 'inherit',
                        textDecoration: 'none',
                        display: 'inline',
                        wordBreak: 'break-word',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Check if it's Ctrl+click or Cmd+click for new tab
                        if (e.ctrlKey || e.metaKey) {
                          window.open(`/campaigns/details/${record.id}`, '_blank');
                        } else {
                          history.push(`/campaigns/details/${record.id}`);
                        }
                      }}
                      onAuxClick={(e) => {
                        // Handle middle-click (mouse wheel click)
                        if (e.button === 1) {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(`/campaigns/details/${record.id}`, '_blank');
                        }
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </a>
                    {getIsRequestChange === 'Request Change on Campaign' && (
                      <Tooltip title={getIsRequestChange}>
                        <Tag color="orange" style={{ marginLeft: '5px' }}>
                          update!
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                </Col>
              </Row>
            </Frag>
          );
        },
        sorter: true,
        sortOrder: getSortOrder('name'),
      };
    },
    Status: (field) => {
      return {
        title: <TableColumnTitle text={'Status'} />,
        dataIndex: 'status',
        key: 'status',
        width: '6%',
        align: 'left',
        footerContent: '',
        render: (text, record) => {
          return (
            <Row type="flex" justify="start">
              <Col>
                <StatusTag
                  startDate={
                    record && moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                      ? moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                      : 'N/A'
                  }
                  endDate={
                    record && moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                      ? moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                      : 'N/A'
                  }
                  attributionWindow={
                    record && record.attributionWindow ? record.attributionWindow : 'N/A'
                  }
                  statusCampaign={record.status && record.status}
                />
              </Col>
            </Row>
          );
        },
      };
    },
    'Days Remaining': (field) => {
      return {
        title: <TableColumnTitle text={'Days Remaining'} />,
        dataIndex: 'endDate',
        key: 'endDate',
        align: 'left',
        width: '8%',
        footerContent: '',
        render: (text, record) => {
          const { pacingData } = record;
          if (pacingData && pacingData.daysLeft !== undefined && pacingData.daysLeft !== null) {
            return <FlightDateText text={pacingData.daysLeft.toString()} />;
          } else {
            const endDate = moment(record.endDate).startOf('day');
            const today = moment().startOf('day');
            if (endDate.isBefore(today)) {
              return <FlightDateText text={'0'} />;
            }
            const diffDays = endDate.diff(today, 'days');
            return <FlightDateText text={diffDays.toString()} />;
          }
        },
        sortOrder: getSortOrder('endDate'),
        sorter: (a, b) => {
          const endDateA = moment(a.endDate);
          const endDateB = moment(b.endDate);

          // DESC
          if (endDateA.isBefore(endDateB)) {
            return -1;
          }
          // ASC
          if (endDateA.isAfter(endDateB)) {
            return 1;
          }
          return 0;
        },
      };
    },
    'Start Date': (field) => {
      return {
        title: <TableColumnTitle text={'Start Date'} />,
        dataIndex: 'startDate',
        key: 'startDate',
        align: 'left',
        width: '7%',
        footerContent: ' ',
        render: (text, record) => {
          if (!text) {
            return <div />;
          }
          const startDate = record.startDate ? moment(record.startDate).tz(timezone) : null;
          return <FlightDateText text={startDate ? startDate.format('MM/DD/YY') : null} />;
        },
        // Custom Sorter
        sorter: (a, b) => {
          const endDateA = moment(a.endDate);
          const endDateB = moment(b.endDate);

          // DESC
          if (endDateA.isBefore(endDateB)) {
            return -1;
          }
          // ASC
          if (endDateA.isAfter(endDateB)) {
            return 1;
          }
          return 0;
        },
        sortOrder: getSortOrder('startDate'),
      };
    },
    'End Date': (field) => {
      return {
        title: <TableColumnTitle text={'End Date'} />,
        dataIndex: 'endDate',
        key: 'endDate',
        align: 'left',
        width: '7%',
        footerContent: ' ',
        render: (text, record) => {
          if (!text) {
            return <div />;
          }
          const endDate = record.endDate ? moment(record.endDate).tz(timezone) : null;
          return <FlightDateText text={endDate ? endDate.format('MM/DD/YY') : null} />;
        },
        // Custom Sorter
        sorter: (a, b) => {
          const endDateA = moment(a.endDate);
          const endDateB = moment(b.endDate);

          // DESC
          if (endDateA.isBefore(endDateB)) {
            return -1;
          }
          // ASC
          if (endDateA.isAfter(endDateB)) {
            return 1;
          }
          return 0;
        },
        sortOrder: getSortOrder('endDate'),
      };
    },
    Performance: (field) => {
      return {
        title: <TableColumnTitle text={'Performance'} />,
        dataIndex: 'performance',
        key: 'performanceStatus',
        align: 'left',
        width: '12%',
        footerStyle: 'block',
        ellipsis: true,
        footerSums: [
          {
            property: 'aggregateData.overallData.aggImpressions',
            formatter: '',
            title: 'Impressions',
          },
          {
            property: 'aggregateData.overallData.aggClicks',
            formatter: '',
            title: 'Clicks',
          },
        ],
        render: (text, { aggregateData, data }) => {
          return (
            <Row type="flex">
              <Col style={{ minWidth: '50px' }}>
                {!isEmpty(aggregateData) ? (
                  <>
                    <Row type="flex">
                      <Tooltip title="Cumulative Impressions (since launch)">
                        <span>
                          <PrimaryTableRowText text="Impressions: " />
                        </span>
                      </Tooltip>
                      <StandardTableRowText
                        text={` ${Number(
                          parseInt(aggregateData.overallData.aggImpressions).toFixed(0)
                        ).toLocaleString()}`}
                      />
                    </Row>
                    <Row type="flex">
                      <PrimaryTableRowText text={`Clicks:\u00A0‏‏‎‎`} />
                      <StandardTableRowText
                        text={`${Number(
                          parseInt(aggregateData.overallData.aggClicks).toFixed(0)
                        ).toLocaleString()}`}
                      />
                    </Row>
                    <Row type="flex">
                      <PrimaryTableRowText text={`CTR:\u00A0‏‏‎‎`} />
                      <StandardTableRowText
                        text={`${getCTRFromAggregate(aggregateData.overallData)}%`}
                      />
                    </Row>
                  </>
                ) : (
                  <PrimaryTableRowText text={`-`} />
                )}
              </Col>
            </Row>
          );
        },
      };
    },
    Budget: (field) => {
      let footerSums = [
        {
          property: 'budget',
          isCurrency: true,
          title: 'Budget',
        },
        {
          property: 'impressionsGoal',
          formatter: '',
          title: 'Impressions Goal',
        },
      ];
      let footerStyle = 'block';
      if (switchStateBudget) {
        footerSums = [
          {
            property: 'budgetSpent',
            isCurrency: true,
            title: '',
          },
          {
            property: 'budget',
            formatter: 'spent of ',
            isCurrency: true,
            title: '',
          },
        ];
        footerStyle = 'inline';
      }
      const tooltipDescription = `𝗗𝗲𝘁𝗮𝗶𝗹𝘀: Displays Budget, Impressions Goal, & Cost per mille (CPM)
      𝗣𝗿𝗼𝗴𝗿𝗲𝘀𝘀: Display progress bar indicator of your remaining budget (I.E 100% means budget is totally spent)
      `;

      return {
        title: (
          <React.Fragment>
            <TableColumnTitle text={'Budget'} />
            <Switch
              checked={switchStateBudget}
              onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
              }}
              onChange={(checked, e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                isColumnChange = true;
                setSwitchStateBudget(checked);
              }}
            />
          </React.Fragment>
        ),
        dataIndex: 'budget',
        key: `budget`,
        align: 'left',
        width: '15%',
        ellipsis: true,
        footerSums: footerSums,
        footerStyle: footerStyle,
        sorter: true,
        showSorterTooltip: {
          title: (
            <div>
              {tooltipDescription}
              <br />
              <br />
              Click column title to sort values, toggle switch to view progress bar.
            </div>
          ),
        },
        render: (budget, row) => {
          const { aggregateData } = row;
          const cpm = getEffectiveCpm(row);
          let impressionGoal = 0;
          let impressions = 0;
          try {
            impressions = aggregateData.overallData.aggImpressions;
          } catch (e) {
            impressions = 0;
          }

          if (cpm && budget) {
            impressionGoal = (budget / cpm) * 1000;
          }
          const budgetSpent = getCampaignSpend(row);
          const budgetRemaining = budget - budgetSpent;
          const budgetRemainingPercentage = (budgetRemaining / budget) * 100;
          const percentageProgress = 100 - budgetRemainingPercentage;

          const pricingModel = row.pricingModel;
          if (!switchStateBudget) {
            if (pricingModel === "Percentage") {
              return (
                <Row type="flex" justify="start">
                  <Col>
                    <Row type="flex">
                      <PrimaryTableRowText text={`Budget:\u00A0‏‏‎‎`} />
                      <StandardTableRowText text={`${formatCurrency(budget, currencyFormat)}`} />
                    </Row>
                  </Col>
                </Row>
              );
            } else {
              return (
                <Row type="flex" justify="start">
                  <Col>
                    <Row type="flex">
                      <PrimaryTableRowText text={`Budget:\u00A0‏‏‎‎`} />
                      <StandardTableRowText text={`${formatCurrency(budget, currencyFormat)}`} />
                    </Row>

                    <Row type="flex">
                      <PrimaryTableRowText text={`Impressions Goal:\u00A0‎`} />
                      <StandardTableRowText
                        text={
                          ` ${Number(parseInt(impressionGoal).toFixed(0)).toLocaleString()}` || '0'
                        }
                      />
                    </Row>

                    <Row type="flex">
                      <PrimaryTableRowText text={`CPM:\u00A0‎`} />
                      <span>
                        <StandardTableRowText
                          text={
                            cpm !== null
                              ? `${currencyFormat.symbol}${
                              // check if cpm has cents (decimals) using modulo
                              // show decimals only if cpm has cents value
                              cpm % 1 !== 0 ? cpm.toFixed(2) : Math.floor(cpm)
                              }`
                              : `${currencyFormat.display}0`
                          }
                        />
                      </span>
                    </Row>
                  </Col>
                </Row>
              );
            }

          }
          return (
            <React.Fragment>
              <PrimaryTableRowText
                text={`${formatCurrency(
                  parseFloat(budgetSpent).toFixed(2),
                  currencyFormat
                )} spent of ${formatCurrency(
                  Number(parseInt(budget <= 1 ? 0 : budget).toFixed(0)),
                  currencyFormat
                )}`}
              />
              <Progress
                style={{
                  fontWeight: 'bold',
                }}
                size="small"
                strokeColor={{
                  from: '#108ee9',
                  to: '#87d068',
                }}
                percent={parseInt(percentageProgress < 0 ? 100 : percentageProgress)}
                status={percentageProgress >= 100 ? 'success' : 'active'}
              />
              {impressions > impressionGoal && (
                <>
                  <Tag color="green" style={{ marginTop: '8px' }}>
                    Value added impressions: <UserAddOutlined />
                    {' +'}
                    {Number(parseInt(impressions - impressionGoal).toFixed(0)).toLocaleString()}
                  </Tag>
                </>
              )}
            </React.Fragment>
          );
        },
        sortOrder: getSortOrder('budget'),
      };
    },
    //Preserved for referencing - TODO - Implement profit / loss calculation - Currently commented out in constants.js
    Spent: (field) => {
      return {
        title: <TableColumnTitle text={'Spent'} />,
        dataIndex: 'spent',
        key: `aggregateData.overallData.aggTotalCost`,
        align: 'left',
        width: '14%',
        ellipsis: true,
        footerSum: true,
        footerSumFixed: 0,
        footerSumFormatter: '$',
        render: (text, { aggregateData, startDate, endDate, budget }) => {
          const dailyTotalSpent = dailyTotalSpentFromAggregate(aggregateData);
          const overallTotalSpent = totalSpentFromAggregate(aggregateData);
          const pacingGoals = getPacingGoal(startDate, endDate, budget);
          const dailyPacing = getDailyPacing(pacingGoals, dailyTotalSpent);
          return (
            <Row type="flex" justify="start">
              <Col>
                <PrimaryTableRowText
                  text={`${formatCurrency(overallTotalSpent, currencyFormat)}`}
                />
                <Row type="flex" justify="space-between" align="middle">
                  <SecondaryTableRowText text={`24hr Pace:`} />
                  <span style={{ marginLeft: '5px' }}>
                    <SecondaryTableRowText
                      text={dailyPacing ? `${dailyPacing}%` : 'N/A'}
                      color={dailyPacing ? (dailyPacing > 50 ? '#00DE51' : '#EC5D58') : '#BCBDC0'}
                    />
                  </span>
                </Row>
              </Col>
            </Row>
          );
        },
      };
    },
    Lift: (field) => {
      return {
        title: <TableColumnTitle text={'Lift'} />,
        dataIndex: field.key,
        key: `aggregateData.overallData.${field.dataAccessor}`,
        align: 'left',
        width: '8%',
        footerSum: true,
        render: (text, { aggregateData, data }) => {
          return (
            <Row type="flex" justify="start" align="middle">
              <Col style={{ minWidth: '75px' }}>
                {!isEmpty(aggregateData) ? (
                  <PrimaryTableRowText
                    text={`${Number(
                      aggregateData.overallData[field.dataAccessor]
                    ).toLocaleString()}%`}
                  />
                ) : (
                  <PrimaryTableRowText text={`-`} />
                )}
              </Col>
            </Row>
          );
        },
      };
    },
    Pacing: (field) => {
      return {
        title: (
          <>
            <TableColumnTitle text={'Pacing'} />
            <Switch
              checked={switchPacingYesterday}
              onChange={() => {
                isColumnChange = true;
                setSwitchPacingYesterday(!switchPacingYesterday);
              }}
            />
          </>
        ),
        dataIndex: null, //uses chart data
        sorter: true,
        key: `aggregateData.overallData.${field.key}`,
        align: 'left',
        width: '9%',
        footerSum: true,
        showSorterTooltip: {
          title: (
            <div>
              When the switch is toggled ON, it shows the yesterday's pacing. Finished Campaign will
              always show the final pacing
            </div>
          ),
        },
        render: (text, row) => {
          const { aggregateData, pacingData } = row;
          const paceAddtionalInfo = pacingData?.pacing;
          if (paceAddtionalInfo) {
            const status = deriveStatus(
              row && moment(row.startDate).seconds(0).milliseconds(0).toISOString()
                ? moment(row.startDate).seconds(0).milliseconds(0).toISOString()
                : null,
              row && moment(row.endDate).seconds(0).milliseconds(0).toISOString()
                ? moment(row.endDate).seconds(0).milliseconds(0).toISOString()
                : null,
              row && row.attributionWindow ? row.attributionWindow : null,
              row.status && row.status
            );

            if (row.pricingModel === 'Percentage') {
              let pace = pacingData?.pace || 0;
              let budgetSpent = pacingData?.budgetSpent || 0;
              let budgetSpentToday = pacingData?.budgetSpentToday || 0;
              let budgetLeft = pacingData?.budgetLeft || 0;
              let dailyAvgBudgetLeft = pacingData?.dailyAvgBudgetLeft || 0;

              let totalBudget = pacingData?.overallBudget || 0;
              let totalBudgetSpent = pacingData?.overallSpend || 0;
              let textColor = '#ff4d4f'; // danger
              if (pace) {
                switch (true) {
                  case pace <= 100 && pace >= 50:
                    textColor = '#52c41a'; // success
                    break;
                  case pace >= 0 && pace < 50:
                    textColor = '#faad14'; // warning
                    break;
                  default:
                    break;
                }
              }

              if (switchPacingYesterday) {
                pace = pacingData?.paceYesterday || 0;
                dailyAvgBudgetLeft = pacingData?.dailyAvgBudgetYesterday || 0;
                budgetLeft = pacingData?.budgetLeftYesterday || 0;
                budgetSpentToday = pacingData?.budgetSpentYesterday || 0;
                budgetSpent = pacingData?.budgetYesterday || 0;
              }

              // Override computation for finished campaigns
              if (status === 'FINISHED') {
                dailyAvgBudgetLeft = 0;
                budgetLeft = (pacingData?.totalBudget || 0) - (pacingData?.budgetSpent || 0);
                budgetSpentToday = 0;
                budgetSpent = pacingData?.budgetSpent || 0;
              }

              return (
                <Row type="flex" justify="start">
                  <Col>
                    <Text style={primaryTextStyle}>
                      <Tooltip title="Pacing for Percentage pricing model">
                        Pace:{' '}
                        <span style={{ color: textColor }}>
                          {pace !== null ? formatNumber2SigFig(pace) : 0}%
                        </span>
                      </Tooltip>
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <PrimaryTableRowText text="Remaining:" />
                      <PrimaryTableRowText
                        text={formatCurrency2SigFig(budgetLeft)}
                        style={{ wordBreak: 'break-all' }}
                      />
                    </div>
                    <Row type="flex" justify="space-between" align="middle">
                      <Tooltip title="Daily goal">
                        <SecondaryTableRowText text="Expected Daily:" color={'#BCBDC0'} />
                        <SecondaryTableRowText
                          text={formatCurrency2SigFig(dailyAvgBudgetLeft)}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </Row>
                    <Row type="flex" justify="space-between" align="middle">
                      <Tooltip title="Amount spent within the day">
                        <SecondaryTableRowText
                          text={switchPacingYesterday ? 'Spent Yesterday:' : 'Spent Today:'}
                        />
                        <SecondaryTableRowText
                          text={formatCurrency2SigFig(budgetSpentToday ?? 0)}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </Row>
                    <Row type="flex" justify="space-between" align="middle">
                      <Tooltip title="Total amount spent">
                        <SecondaryTableRowText text="Budget Spent:" />
                        <SecondaryTableRowText
                          text={formatCurrency2SigFig(budgetSpent)}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </Row>
                    <Row type="flex" justify="space-between" align="middle">
                      <Tooltip title="Total Budget for the whole duration of the campaign">
                        <SecondaryTableRowText text="Budget:" />
                        <SecondaryTableRowText
                          text={formatCurrency2SigFig(totalBudget)}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </Row>
                    <Row type="flex" justify="space-between" align="middle">
                      <Tooltip title="Total amount spent from the start of the campaign">
                        <SecondaryTableRowText text="Total Budget Spent:" />
                        <SecondaryTableRowText
                          text={formatCurrency2SigFig(totalBudgetSpent)}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </Row>
                  </Col>
                </Row>
              );
            } else {
              let pace = pacingData?.pace || 0;
              let impressionsleft = pacingData?.impressionsLeft || 0;
              let dailyImpressionsGoal = pacingData?.dailyAvgImpressionsLeft || 0;
              let todaysImpressions = paceAddtionalInfo?.[0].impressionServedToday || 0;
              let impressionsSpent = paceAddtionalInfo?.[0].impressionsServed || 0;

              let overallImpressions = pacingData?.overallImpressions || 0;
              let overallDeliveredImpressions = pacingData?.overallDeliveredImressions || 0;
              if (switchPacingYesterday) {
                pace = pacingData && pacingData.paceYesterday;
                impressionsleft = pacingData?.impressionsLeftYesterday || 0;
                dailyImpressionsGoal = pacingData?.dailyAvgImpressionsLeftYesterday || 0;
                todaysImpressions = pacingData?.impressionsYesterday || 0;
                impressionsSpent = pacingData?.totalImpressionsYesterday || 0;
              }

              // Override computation for finished campaigns
              if (status === 'FINISHED') {
                impressionsSpent = aggregateData.overallData.aggImpressions;
                dailyImpressionsGoal = 0;
                pace = pacingData.pace;
                todaysImpressions = 0;
                impressionsleft = pacingData.impressionGoal - impressionsSpent;
              }

              let textColor = '#ff4d4f'; // danger
              if (pace) {
                switch (true) {
                  case pace >= 95:
                    textColor = '#52c41a'; // success
                    break;
                  case pace >= 91 && pace <= 95:
                    textColor = '#faad14'; // warning
                    break;
                  default:
                    break;
                }
              }

              return (
                <Row type="flex" justify="start">
                  <Col style={{ width: '100%' }}>
                    <Text style={primaryTextStyle}>
                      <Tooltip
                        title={`Projected Pace. (Impression from ${switchPacingYesterday ? `Yesterday ` : `Today`}
                          * Days Remaining at the time) + Served Impression at the start of ${switchPacingYesterday ? `Yesterday ` : `Today`} / Impression Goal`}
                      >
                        Pace:{' '}
                        <span style={{ color: textColor }}>
                          {pace !== null ? formatNumber2SigFig(pace) : 0}%
                        </span>
                      </Tooltip>
                    </Text>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <PrimaryTableRowText
                        text={`${impressionsleft < 0 ? 'Overage: ' : 'Remaining: '}`}
                        style={{ marginRight: '4px' }}
                      />
                      <PrimaryTableRowText
                        text={formatWholeNumber(impressionsleft)}
                        style={{
                          flex: '1 1 auto',
                          wordBreak: 'break-all',
                        }}
                      />
                    </div>
                    {status !== 'FINISHED' && (
                      <div style={{ marginTop: '4px' }}>
                        <SecondaryTableRowText
                          text={
                            <Tooltip
                              title={`Number of impressions for the day needed to reach the goal. (Impression Goal - Served Impressions) / Days Remaining`}
                            >
                              {dailyImpressionsGoal
                                ? `Daily: ${formatWholeNumber(dailyImpressionsGoal)}`
                                : 'Daily: 0'}
                            </Tooltip>
                          }
                          color={'#BCBDC0'}
                        />
                      </div>
                    )}

                    {status !== 'FINISHED' && (
                      <div style={{ marginTop: '4px' }}>
                        <SecondaryTableRowText
                          text={
                            <Tooltip title={`Number of impressions served within the day`}>
                              {(switchPacingYesterday ? `Yesterday: ` : `Today:`) +
                                (todaysImpressions ? `${formatWholeNumber(todaysImpressions)}` : '0')}
                            </Tooltip>
                          }
                        />
                      </div>
                    )}

                    <div style={{ marginTop: '4px' }}>
                      <Tooltip
                        title={`Number of impressions served at the start of the day, excluding those served later in the day`}
                      >
                        <SecondaryTableRowText text={`Served Impressions: `} />
                        <SecondaryTableRowText
                          text={impressionsSpent ? `${formatWholeNumber(impressionsSpent)}` : '0'}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </div>

                    <div style={{ marginTop: '4px' }}>
                      <Tooltip title={`Number of impressions served from the start of the campaign`}>
                        <SecondaryTableRowText text={`Total Delivered: `} />
                        <SecondaryTableRowText
                          text={
                            overallDeliveredImpressions
                              ? `${formatWholeNumber(overallDeliveredImpressions)}`
                              : '0'
                          }
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </div>

                    <div style={{ marginTop: '4px' }}>
                      <Tooltip title={`Total Impression goal for the whole duration of the campaign`}>
                        <SecondaryTableRowText text={`Impression Goal: `} />
                        <SecondaryTableRowText
                          text={overallImpressions ? `${formatWholeNumber(overallImpressions)}` : '0'}
                          color={'#BCBDC0'}
                        />
                      </Tooltip>
                    </div>
                  </Col>
                </Row>
              );
            }
          } else {
            // Run pacing legacy columns
            const status = deriveStatus(
              row && moment(row.startDate).seconds(0).milliseconds(0).toISOString()
                ? moment(row.startDate).seconds(0).milliseconds(0).toISOString()
                : null,
              row && moment(row.endDate).seconds(0).milliseconds(0).toISOString()
                ? moment(row.endDate).seconds(0).milliseconds(0).toISOString()
                : null,
              row && row.attributionWindow ? row.attributionWindow : null,
              row.status && row.status
            );
            let pace = pacingData && pacingData.pace;
            let daysLeft = pacingData && pacingData.daysLeft;
            if (switchPacingYesterday) {
              pace = pacingData && pacingData.paceYesterday;
            }

            if (aggregateData && !isEmpty(aggregateData)) {
              const { byDate } = aggregateData;
              const chartData = byDate;
              if (byDate && !isEmpty(byDate)) {
                const { budget, aggregateData } = row;
                const cpm = getEffectiveCpm(row);
                if (!budget || !cpm || !budget) {
                  return (
                    <Row type="flex" justify="start">
                      <Col>
                        <PrimaryTableRowText text={`N/A`} />
                      </Col>
                    </Row>
                  );
                }

                let impressionsSpent = 0;
                if (aggregateData && aggregateData.overallData) {
                  const latestDate = chartData.reduce((latest, curr) => {
                    const { xAxis } = curr;
                    if (!latest) {
                      return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
                    }
                    if (moment(xAxis, 'YYYY-MM-DD').isAfter(latest, 'day')) {
                      return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
                    }
                    return latest;
                  }, moment(row.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));

                  impressionsSpent = chartData.reduce((acc, curr) => {
                    const { xAxis, impressions } = curr;
                    const dateYesterday = moment(latestDate, 'YYYY-MM-DD').subtract(
                      switchPacingYesterday ? 2 : 1,
                      'days'
                    );
                    if (moment(xAxis, 'YYYY-MM-DD').isSameOrBefore(dateYesterday, 'day')) {
                      acc += impressions;
                    }
                    return acc;
                  }, 0);
                }

                let dailyImpressionsGoal = 0;
                let impressionGoal = 0;
                if (cpm && budget && budget !== 1) {
                  impressionGoal = (budget / cpm) * 1000;
                }

                let impressionsleft = impressionGoal - impressionsSpent;

                let todaysImpressions = 0;

                if (switchPacingYesterday) todaysImpressions = pacingData.impressionsYesterday;
                else todaysImpressions = chartData[chartData.length - 1].impressions;

                if (daysLeft && impressionsleft) {
                  if (switchPacingYesterday) daysLeft += 1; // Since we are using yesterday's pacing add 1 on days left
                  dailyImpressionsGoal = impressionsleft / daysLeft;
                } else dailyImpressionsGoal = 0;

                // Override computation for finished campaigns
                if (status === 'FINISHED' || status === 'ATTRIBUTION') {
                  impressionsSpent = aggregateData.overallData.aggImpressions;
                  dailyImpressionsGoal = 0;
                  pace = (impressionsSpent / impressionGoal) * 100;
                  todaysImpressions = 0;
                  impressionsleft = impressionGoal - impressionsSpent;
                }

                let textColor = '#ff4d4f'; // danger
                if (pace) {
                  switch (true) {
                    case pace >= 95:
                      textColor = '#52c41a'; // success
                      break;
                    case pace >= 91 && pace <= 95:
                      textColor = '#faad14'; // warning
                      break;
                    default:
                      break;
                  }
                }

                return (
                  <Row type="flex" justify="start">
                    <Col style={{ width: '100%' }}>
                      <Text style={primaryTextStyle}>
                        <Tooltip
                          title={`Projected Pace. (Impression from ${switchPacingYesterday ? `Yesterday ` : `Today`}
                          * Days Remaining at the time) + Served Impression at the start of ${switchPacingYesterday ? `Yesterday ` : `Today`} / Impression Goal`}
                        >
                          Pace:{' '}
                          <span style={{ color: textColor }}>
                            {pace !== null ? formatNumber2SigFig(pace) : 0}%
                          </span>
                        </Tooltip>
                      </Text>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          width: '100%',
                        }}
                      >
                        <PrimaryTableRowText
                          text={`${impressionsleft < 0 ? 'Overage: ' : 'Remaining: '}`}
                          style={{ marginRight: '4px' }}
                        />
                        <PrimaryTableRowText
                          text={formatWholeNumber(impressionsleft)}
                          style={{
                            flex: '1 1 auto',
                            wordBreak: 'break-all',
                          }}
                        />
                      </div>
                      {status !== 'FINISHED' && (
                        <div style={{ marginTop: '4px' }}>
                          <SecondaryTableRowText
                            text={
                              <Tooltip
                                title={`Number of impressions for the day needed to reach the goal. (Impression Goal - Served Impressions) / Days Remaining`}
                              >
                                {dailyImpressionsGoal
                                  ? `Daily: ${formatWholeNumber(dailyImpressionsGoal)}`
                                  : 'Daily: 0'}
                              </Tooltip>
                            }
                            color={'#BCBDC0'}
                          />
                        </div>
                      )}

                      {status !== 'FINISHED' && (
                        <div style={{ marginTop: '4px' }}>
                          <SecondaryTableRowText
                            text={
                              <Tooltip title={`Number of impressions served within the day`}>
                                {(switchPacingYesterday ? `Yesterday: ` : `Today:`) +
                                  (todaysImpressions
                                    ? `${formatWholeNumber(todaysImpressions)}`
                                    : '0')}
                              </Tooltip>
                            }
                          />
                        </div>
                      )}

                      <div style={{ marginTop: '4px' }}>
                        <Tooltip
                          title={`Number of impressions served at the start of the day, excluding those served later in the day`}
                        >
                          <SecondaryTableRowText text={`Served Impressions: `} />
                          <SecondaryTableRowText
                            text={impressionsSpent ? `${formatWholeNumber(impressionsSpent)}` : '0'}
                            color={'#BCBDC0'}
                          />
                        </Tooltip>
                      </div>
                    </Col>
                  </Row>
                );
              }
            } else {
              return (
                <Row type="flex" justify="start">
                  <Col>
                    <PrimaryTableRowText text={`N/A`} />
                  </Col>
                </Row>
              );
            }
          }
        },
      };
    },
    Sales: (_field) => {
      const tooltipDescription =
        'Details: Shows a mini-chart of transaction sales when the switch is toggled to the ON position.';

      let footerSums = [];

      if (!switchStateSales) {
        footerSums = [
          {
            property: 'aggregateData.overallData.aggEvents.transactionsTotalCount',
            formatter: '',
            title: 'Orders',
          },
          {
            property: 'aggregateData.overallData.aggEvents.transactionTotalRevenue',
            isCurrency: true,
            title: 'Rev',
          },
        ];
      }

      return {
        title: (
          <React.Fragment>
            <TableColumnTitle text={'Sales'} />
            <Switch
              checked={switchStateSales}
              onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
              }}
              onChange={(checked, e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                setSwitchStateSales(checked);
              }}
            />
          </React.Fragment>
        ),
        dataIndex: 'Sales',
        key: `Sales`,
        align: 'left',
        width: '8%',
        footerStyle: 'block',
        footerSums: footerSums,
        sorter: (a, b) => getCampaignRoas(a) - getCampaignRoas(b),
        showSorterTooltip: {
          title: (
            <div>
              {tooltipDescription}
              <br />
              <br />
              Click column title to sort values, toggle switch to view mini-chart.
            </div>
          ),
        },
        sortOrder: getSortOrder('Sales'),
        render: (text, row) => {
          const { aggregateData, attributionTargets, cpm } = row;
          const latestTransactionDate = getLatestTransactionDate(aggregateData);
          if (!switchStateSales) {
            return (
              <Row type="flex" justify="start" align="middle">
                <Col style={{ minWidth: '50px' }}>
                  {!isEmpty(aggregateData) ? (
                    <>
                      <Row type="flex">
                        <PrimaryTableRowText text={`Orders:‏‏‎ ‎`} />
                        <StandardTableRowText
                          text={
                            aggregateData &&
                              aggregateData.overallData &&
                              aggregateData.overallData.aggEvents &&
                              aggregateData.overallData.aggEvents.transactionsTotalCount
                              ? aggregateData.overallData.aggEvents.transactionsTotalCount
                              : 0
                          }
                        />
                      </Row>
                      <Row type="flex">
                        <PrimaryTableRowText text={`Rev:‏‏‎ ‎`} />
                        <StandardTableRowText
                          text={formatCurrency(
                            aggregateData &&
                              aggregateData.overallData &&
                              aggregateData.overallData.aggEvents &&
                              aggregateData.overallData.aggEvents.transactionTotalRevenue
                              ? parseFloat(
                                aggregateData.overallData.aggEvents.transactionTotalRevenue
                              )
                              : 0,
                            currencyFormat
                          )}
                        />
                      </Row>
                      <Row type="flex">
                        <PrimaryTableRowText text={`ROAS:‏‏‎ ‎`} />
                        <StandardTableRowText
                          text={(() => {
                            const roas = getCampaignRoas(row);
                            return roas > 0 ? roas.toFixed(2) + '%' : 'N/A';
                          })()}
                        />
                      </Row>
                      <Row type="flex">
                        <PrimaryTableRowText text={`Latest Date:‏‏‎ ‎`} />
                        <StandardTableRowText
                          text={latestTransactionDate ? latestTransactionDate : 'N/A'}
                        />
                      </Row>
                    </>
                  ) : (
                    <PrimaryTableRowText text={`-`} />
                  )}
                </Col>
              </Row>
            );
          }
          return (
            <Row type="flex" justify="center" align="middle">
              <Col>
                <MiniChart
                  data={
                    aggregateData &&
                      aggregateData.overallData &&
                      aggregateData.overallData.aggEvents &&
                      aggregateData.overallData.aggEvents.transactionsByDate &&
                      Array.isArray(aggregateData.overallData.aggEvents.transactionsByDate) &&
                      aggregateData.overallData.aggEvents.transactionsByDate.length > 0
                      ? aggregateData.overallData.aggEvents.transactionsByDate
                      : []
                  }
                  yAxisLeft="transactionsTotalCount"
                />
                {(() => {
                  return (
                    latestTransactionDate && (
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#8c8c8c' }}>
                        Latest: {latestTransactionDate}
                      </div>
                    )
                  );
                })()}
              </Col>
            </Row>
          );
        },
      };
    },
    'Sign Ups': (field) => {
      return {
        title: <TableColumnTitle text={'Sign Ups'} />,
        dataIndex: null,
        key: `aggregateData.overallData.${field.key}`,
        align: 'left',
        width: '8%',
        ellipsis: true,
        footerSums: [
          {
            property: 'aggregateData.overallData.signupsOverallData',
            formatter: '',
            title: 'Sign Ups',
          },
        ],
        render: (text, { aggregateData, attributionTargets }) => {
          return (
            <Row type="flex" justify="start" align="middle">
              <Col style={{ minWidth: '50px' }}>
                {!isEmpty(aggregateData) ? (
                  <>
                    <Row type="flex">
                      <PrimaryTableRowText text={`Sign Up:‏‏‎ ‎`} />
                      <StandardTableRowText
                        text={`${Number(
                          parseInt(
                            aggregateData.overallData.signupsOverallData === null
                              ? 0
                              : aggregateData.overallData.signupsOverallData
                          ).toFixed(0)
                        ).toLocaleString()}`}
                      />
                    </Row>
                  </>
                ) : (
                  <PrimaryTableRowText text={`-`} />
                )}
              </Col>
            </Row>
          );
        },
      };
    },
    Profit: (field) => {
      return {
        title: <TableColumnTitle text={'Profit'} />,
        dataIndex: 'Profit',
        key: `Profit`,
        align: 'left',
        width: '10%',
        footerStyle: 'block',
        render: (text, record) => {
          const {
            advertiser,
            campaigns = [],
            aggregateData,
            externalCRMIntegrations = [],
          } = record;

          // Ensure campaigns is always an array (GraphQL may return null in error scenarios)
          const safeCampaigns = campaigns ?? [];

          // Check if contract is enabled
          const hasContract =
            advertiser &&
            advertiser.pricingModel &&
            advertiser.pricingModel.hasContract;

          if (!hasContract) {
            return (
              <Row type="flex" justify="start" align="middle">
                <Col>
                  <PrimaryTableRowText text={`-`} />
                </Col>
              </Row>
            );
          }

          // Get pricing model from advertiser level
          const pricingModel = advertiser.pricingModel.model;

          // Calculate Total Budget
          let totalBudget = 0;
          if (externalCRMIntegrations && externalCRMIntegrations.length > 0) {
            // If there are multiple IOs, use totalPrice from externalCRMIntegrations
            totalBudget = externalCRMIntegrations.reduce(
              (sum, integration) => sum + (integration.totalPrice || 0),
              0
            );
          } else {
            // Otherwise, aggregate budget from all campaigns
            totalBudget = safeCampaigns.reduce(
              (sum, campaign) => sum + (campaign.budgetTotal || 0),
              0
            );
          }

          let spent = 0;
          let expectedProfit = 0;
          let advertiserBudget = 0;
          let profit = 0;

          if (pricingModel === 'Percentage') {
            // For percentage price model
            // Spent: Use Aggregated Total Cost from Nexxen Display Report
            spent = aggregateData?.overallData?.aggTotalCost || 0;

            // Calculate per-campaign expected profit and advertiser budget using campaign-level percentages
            safeCampaigns.forEach((campaign) => {
              const campaignBudget = campaign.budgetTotal || 0;
              const advPercentage = parseFloat(campaign.advertiserPercentage || 0) / 100;
              const contPercentage = parseFloat(campaign.contractPercentage || 0) / 100;

              // Expected Profit per campaign: Budget × (advertiserPercentage - contractPercentage)
              expectedProfit += campaignBudget * (advPercentage - contPercentage);

              // Advertiser Budget per campaign: Budget × (1 - contractPercentage)
              advertiserBudget += campaignBudget * (1 - contPercentage);
            });

            // Profit: Advertiser Budget - Spent
            profit = advertiserBudget - spent;
          } else if (pricingModel === 'CPM') {
            // For CPM price model
            // Calculate per-campaign using campaign-level contractCPM
            safeCampaigns.forEach((campaign) => {
              const impressions = campaign.aggregateData?.overallData?.aggImpressions || 0;
              const cpm = campaign.cpm || 0;
              const contractCPM = parseFloat(campaign.contractCPM || 0);
              const campaignBudget = campaign.budgetTotal || 0;

              // Spent per campaign: (impressions / 1000) × cpm
              spent += (impressions / 1000) * cpm;

              // Expected Profit per campaign: Budget × ((Contract CPM - CPM) / Contract CPM)
              if (contractCPM > 0) {
                expectedProfit += campaignBudget * ((contractCPM - cpm) / contractCPM);
              }
            });

            // Advertiser Budget: Use totalBudget (respects externalCRMIntegrations or campaign budgets)
            advertiserBudget = totalBudget;

            // Profit: Advertiser Budget - Spent
            profit = advertiserBudget - spent;
          } else {
            return (
              <Row type="flex" justify="start" align="middle">
                <Col>
                  <PrimaryTableRowText text={`N/A`} />
                </Col>
              </Row>
            );
          }

          // Display Spent, Expected Profit, and Profit
          return (
            <Row type="flex" justify="start" align="middle">
              <Col style={{ minWidth: '50px' }}>
                <Row type="flex">
                  <PrimaryTableRowText text={`Spent:‏‏‎ ‎`} />
                  <StandardTableRowText
                    text={formatCurrency(spent, currencyFormat)}
                  />
                </Row>
                <Row type="flex">
                  <PrimaryTableRowText text={`Expected Profit:‏‏‎ ‎`} />
                  <StandardTableRowText
                    text={formatCurrency(expectedProfit, currencyFormat)}
                  />
                </Row>
                <Row type="flex">
                  <PrimaryTableRowText text={`Profit:‏‏‎ ‎`} />
                  <SecondaryTableRowText
                    text={formatCurrency(profit, currencyFormat)}
                    color={profit >= 0 ? '#52c41a' : '#ff4d4f'}
                  />
                </Row>
              </Col>
            </Row>
          );
        },
      };
    },

    generic: (field) => {
      return {
        title: <TableColumnTitle text={field.title} />,
        dataIndex: field.key,
        key: `aggregateData.overallData.${field.dataAccessor}`,
        align: 'center',
        width: '8%',

        render: (text, { aggregateData, data }) => {
          return (
            <Row type="flex" justify="center" align="middle">
              <Col style={{ minWidth: '75px' }}>
                {!isEmpty(aggregateData) && field.title !== 'Impressions' ? (
                  <PrimaryTableRowText
                    text={`${Number(
                      aggregateData.overallData[field.dataAccessor]
                    ).toLocaleString()}`}
                  />
                ) : (
                  field.title !== 'Impressions' && <PrimaryTableRowText text={`-`} />
                )}
              </Col>
              {field.title === 'Impressions' && (
                <Col>
                  <MiniChart
                    data={aggregateData && aggregateData.byDate ? aggregateData.byDate : []}
                    yAxisLeft={field.title.toLowerCase()}
                  />
                  {(() => {
                    const latestImpressionDate = getLatestImpressionDate(aggregateData);
                    return (
                      latestImpressionDate && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#8c8c8c' }}>
                          Latest: {latestImpressionDate}
                        </div>
                      )
                    );
                  })()}
                </Col>
              )}
            </Row>
          );
        },
      };
    },
    Attribution: (field) => {
      return {
        title: <TableColumnTitle text={field.title} />,
        dataIndex: '',
        key: '',
        align: 'left',
        width: '8%',
        footerContent: ' ',

        render: (
          data,
          {
            attributionTargets,
            transactionTarget,
            transactionsConfig,
            signUpCampaign,
            sessionConfig,
          }
        ) => {
          const transactionIcon = () => {
            if (transactionTarget) {
              if (
                transactionsConfig &&
                transactionsConfig.traffId &&
                transactionsConfig.tracksBasketItems
              ) {
                return (
                  <span
                    title={transactionsConfig && transactionsConfig.appId}
                    role="img"
                    className="anticon"
                    style={{ verticalAlign: '-5px' }}
                  >
                    <ShoppingCartBasketItemLocation
                      style={{
                        width: '1.6rem',
                        height: 'auto',
                        opacity: '1.0',
                      }}
                    />
                  </span>
                );
              }
              if (transactionsConfig && transactionsConfig.tracksBasketItems) {
                return (
                  <span
                    title={transactionsConfig && transactionsConfig.appId}
                    role="img"
                    className="anticon"
                    style={{ verticalAlign: '-5px' }}
                  >
                    <ShoppingCartBasketItem
                      style={{
                        width: '1.6rem',
                        height: 'auto',
                        opacity: '1.0',
                      }}
                    />
                  </span>
                );
              }

              if (transactionsConfig && transactionsConfig.traffId) {
                return (
                  <span
                    title={transactionsConfig && transactionsConfig.appId}
                    role="img"
                    className="anticon"
                    style={{ verticalAlign: '-5px' }}
                  >
                    <ReactLogo
                      style={{
                        fontSize: '30px',
                        opacity: '1.0',
                      }}
                    />
                  </span>
                );
              } else {
                return (
                  <ShoppingCartOutlined
                    title={transactionsConfig && transactionsConfig.appId}
                    style={{
                      fontSize: '30px',
                      opacity: '1.0',
                    }}
                  />
                );
              }
            } else {
              return (
                <ShoppingCartOutlined
                  style={{
                    fontSize: '30px',
                    opacity: '0.2',
                  }}
                />
              );
            }
          };

          const signUpIcon = () => (
            <SignUpEnabled
              style={{
                width: '1.6rem',
                height: 'auto',
                opacity: signUpCampaign ? '1.0' : '0.2',
              }}
            />
          );

          const sessionsIcon = () => (
            <EyeOutlined
              style={{
                fontSize: '1.6rem',
                opacity: sessionConfig && sessionConfig.isSession ? '1.0' : '0.2',
              }}
            />
          );

          return (
            <Row>
              <Col style={{ minWidth: '100px' }}>
                <Space align="center">
                  {transactionIcon()}
                  {signUpIcon()}
                  {sessionsIcon()}
                </Space>
              </Col>
            </Row>
          );
        },
      };
    },
  };

  const getCampaignSpend = (row) => {
    if (row.pricingModel === "Percentage") { 
      return totalSpentFromAggregate(row?.aggregateData) || 0;
    } else {
      const cpm = getEffectiveCpm(row);
      const impressions = row?.aggregateData?.overallData?.aggImpressions || 0;
      const spend = cpm * (impressions / 1000);

      return spend
    }

  };

  const getCampaignRoas = (row) => {
    if (row?.pricingModel === 'Percentage') {
      const spend = getCampaignSpend(row);
      const revenue = row?.aggregateData?.overallData?.aggEvents?.transactionTotalRevenue || 0;
      return spend > 0 ? (revenue / spend) * 100 : 0;
    } else {
      const cpm = getEffectiveCpm(row);
      const impressions = row?.aggregateData?.overallData?.aggImpressions || 0;
      const spend = cpm * (impressions / 1000);
      const revenue = row?.aggregateData?.overallData?.aggEvents?.transactionTotalRevenue || 0;
      return spend > 0 ? (revenue / spend) * 100 : 0;
    }

  };

  const getLatestTransactionDate = (aggregateData) => {
    const transactions = aggregateData?.overallData?.aggEvents?.transactionsByDate;
    if (!transactions?.length) return null;

    const latestTransaction = transactions
      .filter((transaction) => transaction?.transactionsTotalCount > 0 && transaction?.xAxis)
      .reduce((latest, current) => {
        const currentDate = moment(current.xAxis);
        const latestDate = latest ? moment(latest.xAxis) : null;
        return !latestDate || currentDate.isAfter(latestDate) ? current : latest;
      }, null);

    return latestTransaction?.xAxis ? moment(latestTransaction.xAxis).format('MM/DD/YY') : null;
  };

  const getLatestImpressionDate = (aggregateData) => {
    const impressionData = aggregateData?.byDate;
    if (!impressionData?.length) return null;

    const latestImpression = impressionData
      .filter((day) => day?.impressions > 0 && day?.xAxis)
      .reduce((latest, current) => {
        const currentTimestamp = Date.parse(current.xAxis);
        const latestTimestamp = latest ? Date.parse(latest.xAxis) : null;
        return !latestTimestamp || currentTimestamp > latestTimestamp ? current : latest;
      }, null);

    return latestImpression?.xAxis
      ? moment(Date.parse(latestImpression.xAxis)).format('MM/DD/YY')
      : null;
  };

  const fieldsToRender = Object.keys(possibleFields).filter(
    (field) => selectedTableColumns.indexOf(field) >= 0
  );

  // Use desktop columns for full table view
  const columns = getDesktopColumns(fieldsToRender);

  columns.push({
    title: (
      <TableColumnsDropDown
        onTableColChange={onTableColChange}
        selectedTableColumns={selectedTableColumns}
        possibleFields={possibleFields}
      />
    ),
    dataIndex: 'more',
    key: 'more',
    width: '5%',
    // Todo: Icon for "more" here rotated 90 degrees
    render: (text) => null,
  });

  const dataWithCampaignByDate =
    data &&
    data.map((campaignOrder) => {
      let newCampaignOrder = campaignOrderPreCalculate(campaignOrder);
      return {
        ...newCampaignOrder,
        data: aggregateDisplayReports(newCampaignOrder.campaigns.map((campaign) => campaign.data)),
      };
    });

  const rowSelection = {
    onChange: onRowChange,
    selectedRowKeys,
    columnWidth: '5%',
  };

  // Handle card click to navigate to campaign details
  const handleCardClick = (campaign) => {
    history.push(`/campaigns/details/${campaign.id}`);
  };

  // Handle card selection
  const handleCardSelection = (campaign) => {
    if (selectedRowKeys.includes(campaign.id)) {
      onRowChange(
        selectedRowKeys.filter((key) => key !== campaign.id),
        selectedRows.filter((row) => row.id !== campaign.id)
      );
    } else {
      onRowChange([...selectedRowKeys, campaign.id], [...selectedRows, campaign]);
    }
  };

  // Card layout for both mobile and tablet (≤1199px)
  if (screenSize === 'cards') {
    return (
      <div
        className="campaigns-table-wrapper"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          boxShadow:
            '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
          overflow: 'hidden',
          padding: '16px',
        }}
      >
        {/* Campaign Cards for Mobile */}
        <div style={{ marginBottom: '16px' }}>
          {dataWithCampaignByDate &&
            dataWithCampaignByDate.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                currencyFormat={currencyFormat}
                onCardClick={handleCardClick}
                isSelected={selectedRowKeys.includes(campaign.id)}
                onSelectionChange={handleCardSelection}
                selectedTableColumns={selectedTableColumns}
                possibleFields={possibleFields}
              />
            ))}
        </div>

        {/* Mobile Pagination - Simple Pagination Component */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <Pagination
            current={current}
            total={total}
            pageSize={pageSize}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            showQuickJumper
            size="small"
            onChange={(page, pageSize) => {
              const nextPage = location.pathname.split('/');
              nextPage[3] = page;
              const searchParams = new URLSearchParams(window.location.search);

              if (searchParams.get('sort')) searchParams.delete('sort');
              if (searchParams.get('pageSize')) searchParams.delete('pageSize');

              if (pageSize > 10) {
                searchParams.append('pageSize', pageSize);
              }

              history.push({
                pathname: nextPage.join('/'),
                search: `?${searchParams.toString()}`,
              });
            }}
            onShowSizeChange={(current, size) => {
              const nextPage = location.pathname.split('/');
              nextPage[3] = current;
              const searchParams = new URLSearchParams(window.location.search);

              if (searchParams.get('sort')) searchParams.delete('sort');
              if (searchParams.get('pageSize')) searchParams.delete('pageSize');

              if (size > 10) {
                searchParams.append('pageSize', size);
              }

              history.push({
                pathname: nextPage.join('/'),
                search: `?${searchParams.toString()}`,
              });
            }}
          />
        </div>
      </div>
    );
  }

  // Desktop/Tablet table layout
  return (
    <div
      className="campaigns-table-wrapper"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        boxShadow:
          '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
        overflow: 'hidden',
      }}
    >
      <PaginationTable
        summary={dataWithCampaignByDate}
        rowSelection={rowSelection}
        onRow={(data) => ({
          onClick: (event) => {
            // Only handle checkbox clicks for row selection
            if (event.target.classList.contains('ant-table-selection-column')) {
              // If the clicked row is already selected, deselect it; otherwise, select it
              if (selectedRowKeys.includes(data.id)) {
                onRowChange(
                  selectedRowKeys.filter((key) => key !== data.id),
                  selectedRows.filter((row) => row.id !== data.id)
                );
              } else {
                onRowChange([...selectedRowKeys, data.id], [...selectedRows, data]);
              }
            }
            // Removed general row navigation - now only campaign name will be clickable
          },
        })}
        data={dataWithCampaignByDate}
        paginationSize={pageSize}
        showSizeChanger
        size={'default'}
        columns={columns}
        scroll={{ x: 2000 }}
        total={total}
        current={current}
        pageSize={pageSize}
        bordered={false}
        currencyFormat={currencyFormat}
        onChange={(paginationObject, filtersObject, sorterObject, extra) => {
          if (!isColumnChange) {
            const nextPage = location.pathname.split('/');
            nextPage[3] = paginationObject.current;
            const searchParams = new URLSearchParams(window.location.search);

            if (searchParams.get('sort')) searchParams.delete('sort');
            if (searchParams.get('pageSize')) searchParams.delete('pageSize');

            if (!isEmpty(paginationObject) && paginationObject.pageSize > 10) {
              searchParams.append('pageSize', paginationObject.pageSize);
            }

            if (!isEmpty(sorterObject.column)) {
              searchParams.append(
                'sort',
                `${sorterObject.field}_${isNil(sorterObject.order) || sorterObject.order === 'ascend' ? 'ASC' : 'DESC'
                }`
              );
            }

            history.push({
              pathname: nextPage.join('/'),
              search: `?${searchParams.toString()}`,
            });
          }
        }}
        showAllPaginationOption={false}
      />
    </div>
  );
};

export default withRouter(CampaignsTable);
